import { useNavigate, useParams } from "react-router-dom";
import styled from "styled-components";
import { MouseEvent, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { AnimatePresence } from "framer-motion";

import BackButton from "@/components/BackButton";
import Button from "@/components/Button";
import { PageHeading } from "@/components/Layout/Parts";
import Text from "@/components/Text";
import InfoCard from "./components/InfoCard";
import TestsTable from "./components/TestsTable";
import ContentSpinner from "@/components/ContentSpinner";
import { BPTestingProjectResource } from "@/types/resources";
import { api } from "@/utils/api";
import useAlerts from "@/hooks/useAlerts";
import { BPTestingProjectStatus } from "@/utils/enums";
import BPTestingProjectForm from "../BPTestingProjects/components/BPTestingProjectForm";

const HeadingRow = styled.div`
  display: flex;
  justify-content: space-between;
  button {
    flex-shrink: 0;
  }
`;

const StyledPageHeading = styled(PageHeading)`
  flex: 1;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 16px;
  margin-left: 30px;
`;

const EditButton = styled(Button)`
  border-color: ${({ theme }) => theme.colors.lightGray};
  background: transparent;
  &:hover {
    border-color: rgba(34, 34, 34, 0.25);
  }
`;

const BPTestingProject = () => {
  const [showEditForm, setShowEditForm] = useState(false);

  const { id } = useParams();

  const navigate = useNavigate();

  const alerts = useAlerts();

  const queryClient = useQueryClient();

  const { isLoading, data: project } = useQuery(
    ["bp_testing_projects", { id }],
    () =>
      api.get<never, BPTestingProjectResource>(
        `/v1/editorial/bp-testing-projects/${id}`
      ),
    {
      staleTime: 60 * 1000,
      retry: 2,
      onError: (err) => {
        if ((err as any)?.response?.status === 404) {
          return navigate("/404", {
            replace: true,
          });
        }
        alerts.error();
      },
    }
  );

  const onEnd = async (e: MouseEvent<HTMLButtonElement>) => {
    try {
      e.stopPropagation();

      const confirm = await alerts.confirm(
        `Are you sure you want to end this project?`
      );

      if (!confirm) return;

      await api.put(`/v1/editorial/bp-testing-projects/${project!.id}`, {
        status: BPTestingProjectStatus.ENDED,
      });

      await queryClient.invalidateQueries(["bp_testing_projects"]);

      alerts.success("Project has been successfully ended");
    } catch (e) {
      alerts.error();
    }
  };

  const onDelete = async (e: MouseEvent<HTMLButtonElement>) => {
    try {
      e.stopPropagation();

      const confirm = await alerts.confirm(
        `Are you sure you want to delete this project?`
      );

      if (!confirm) return;

      await api.delete(`/v1/editorial/bp-testing-projects/${project!.id}`);

      queryClient.invalidateQueries(["bp_testing_projects"]);

      navigate("/general/bp-testing-projects/active");

      alerts.success("Project was successfully deleted");
    } catch (e) {
      alerts.error();
    }
  };

  const onEdit = () => setShowEditForm(true);

  if (isLoading || !project) {
    return <ContentSpinner />;
  }

  return (
    <>
      <Text $type="caption" $color="gray">
        EDITORIAL
      </Text>
      <HeadingRow>
        <StyledPageHeading>{project.name}</StyledPageHeading>
        <ActionButtons>
          <EditButton color="secondary" onClick={onEdit}>
            Edit
          </EditButton>
          {project.status === BPTestingProjectStatus.ACTIVE && (
            <Button onClick={onEnd}>Stop project</Button>
          )}
          {project.status === BPTestingProjectStatus.ENDED && (
            <Button onClick={onDelete}>Delete project</Button>
          )}
        </ActionButtons>
      </HeadingRow>
      <BackButton />
      <InfoCard project={project} />
      <TestsTable projectId={project.id} projectName={project.name} />
      <AnimatePresence>
        {showEditForm && (
          <BPTestingProjectForm
            editableProject={project}
            onClose={() => setShowEditForm(false)}
          />
        )}
      </AnimatePresence>
    </>
  );
};

export default BPTestingProject;
